import React, { memo, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, IconButton, useTheme } from '@mui/material';
import { MoreIcon } from '@sharedCR/coreDesignSystem/icons';
import { emailNotificationsNotificationsApi } from 'app/services/api';
import PropType from 'prop-types';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import { blobToBase64 } from 'utils/file';

import { getApplicationSignaturePreviewAction } from 'store/companies/actions';
import { selectEmailSignatureTemplate } from 'store/companies/selectors';

import { ActivitiesTestIds } from '../../../Activities.testIds';
import useHtmlAdjustedToTheme from '../../../hooks/useHtmlAdjustedToTheme';
import { getStyles } from '../../EmailCreationModal.styles';

const defaultArray = [];

const BodyAdditionalContent = ({
  applicationId,
  setIsLoading,
  repliedMessage,
  forwardedLinkedResources = defaultArray,
  inReplyTo = '',
}) => {
  const theme = useTheme();
  const classes = getStyles(theme);
  const dispatch = useDispatch();

  const [linkedStorage, setLinkedStorage] = useState({});

  const emailSignatureTemplate = useSelector(selectEmailSignatureTemplate);

  const [isCollapsed, setIsCollapsed] = useState(!!repliedMessage || !!emailSignatureTemplate);

  useEffect(() => {
    if (!repliedMessage && !emailSignatureTemplate) {
      return;
    }

    setIsCollapsed(true);
  }, [repliedMessage, emailSignatureTemplate]);

  const onCollapsedClick = useCallback(() => setIsCollapsed(false), []);

  useEffectSafeStrictMode(() => {
    if (!forwardedLinkedResources?.length) {
      return;
    }

    forwardedLinkedResources.forEach(({ resourceId }) => {
      emailNotificationsNotificationsApi
        .getLinkedResource({ emailId: inReplyTo, resourceId })
        .then(({ data }) => blobToBase64(data))
        .then(binary => {
          setLinkedStorage(storage => ({
            ...storage,
            [resourceId]: binary,
          }));
        });
    });
  }, [forwardedLinkedResources, inReplyTo]);

  useEffectSafeStrictMode(() => {
    setIsLoading(true);
    dispatch(getApplicationSignaturePreviewAction({ applicationId }))
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [dispatch, setIsLoading, applicationId]);

  const signatureTemplateInnerHTML = useHtmlAdjustedToTheme(emailSignatureTemplate);

  const repliedMessageInnerHTML = useHtmlAdjustedToTheme(repliedMessage, linkedStorage);

  return isCollapsed ? (
    <IconButton
      className="self-start m-12"
      data-testid={ActivitiesTestIds.QUOTED_CONTENT_EXPAND_BUTTON}
      size="small"
      onClick={onCollapsedClick}
    >
      <MoreIcon />
    </IconButton>
  ) : (
    <div className="p-12">
      <Box dangerouslySetInnerHTML={repliedMessageInnerHTML} sx={classes.themeTextColor} />
      <Box dangerouslySetInnerHTML={signatureTemplateInnerHTML} sx={classes.themeTextColor} />
    </div>
  );
};

BodyAdditionalContent.propTypes = {
  applicationId: PropType.string.isRequired,
  setIsLoading: PropType.func.isRequired,
  repliedMessage: PropType.string.isRequired,
  forwardedLinkedResources: PropType.array,
  inReplyTo: PropType.string,
};

export default memo(BodyAdditionalContent);
